/**
 * What is wrong with this clause, kept in two separate boxes on purpose.
 *
 * A defect in the agreement and a defect in our copy of it look identical on a
 * screen and are opposite things at a bargaining table. Clause 35.3 running
 * (a), (b), (b), (d) is the employer's drafting error, and asking them to fix
 * it is free: it costs nothing to concede and it demonstrates the group has
 * read the document more carefully than anyone else in the room. Clause 22.3
 * reading `P = --- s xc -d b` is *our* extraction error. Raise that one across
 * the table and the answer is "our copy says something perfectly sensible",
 * and the credibility spent is spent on our bug.
 *
 * So they never share a container, never share a colour, and each says whose
 * defect it is in its first line rather than in a tooltip.
 */

export interface Defect {
  kind: string;
  label: string | null;
  detail: string;
  evidence: string | null;
  page: number | null;
  confirmed: boolean;
}

export interface Formula {
  label: string;
  expression: string | null;
  variables: Record<string, string>;
  recoveredFrom: string | null;
  note: string | null;
  verifiedAt: string | null;
}

/** Defects in the agreement. Bargaining material, and cheap to ask for. */
export function DraftingDefects({ defects }: { defects: Defect[] }) {
  if (defects.length === 0) return null;

  return (
    /*
      A card in the same geometry as every other panel — 8px radius, hairline,
      12px inset — rather than the 6px chip radius it had. The radius scale
      reserves 4–5 for chips and 8–9 for panels, and a panel drawn at chip
      radius is the kind of drift that makes one token set look like several.

      The stronger rule keeps it distinguishable from the reading cards around
      it without a second colour: this is a finding about the document, not the
      document.
    */
    <section className="card mt-4" style={{ borderColor: 'var(--color-rule-strong)' }}>
      {/* 11px, not the label's native 9px: these two headings are sentences
          rather than panel names, and a sentence set in 9px uppercase mono at
          0.1em tracking is a line people skip. */}
      <h3 className="card-header label text-[11px]">
        {defects.length === 1 ? 'A defect in the agreement' : 'Defects in the agreement'} · free to
        ask to fix
      </h3>
      <ul>
        {defects.map((defect) => (
          <li key={`${defect.kind}-${defect.label}`} className="row flex-col items-start gap-1">
            <p className="text-[12.5px] leading-snug">{defect.detail}</p>
            {defect.evidence && (
              // Verbatim, so a technical correction can quote the page rather
              // than paraphrase it.
              <p className="register-reading text-[12px] leading-relaxed text-[var(--color-muted)]">
                “{defect.evidence.trim()}”
                {defect.page ? ` — p${defect.page}` : ''}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Defects in our copy. Not bargaining material — a warning that what is on
 * screen is not what the page says.
 */
export function ExtractionArtefacts({
  artefacts,
  formulas,
}: {
  artefacts: Defect[];
  formulas: Formula[];
}) {
  const recovered = formulas.filter((f) => f.expression !== null);
  const unrecovered = formulas.filter((f) => f.expression === null);

  if (artefacts.length === 0 && formulas.length === 0) return null;

  return (
    /* Panel geometry from `.card`, with the defect colour kept on the border
       and on the heading — it is the one thing on the page that says the words
       below are not the words on the page. */
    <section className="card mt-4" style={{ borderColor: 'var(--color-defect)' }}>
      <h3 className="card-header label text-[11px]" style={{ color: 'var(--color-defect)' }}>
        Our copy of this clause is damaged · the agreement itself is fine
      </h3>

      {artefacts.map((artefact) => (
        <p key={artefact.label} className="mb-2 text-[12.5px] leading-snug">
          {artefact.detail}
          {artefact.page ? (
            <span className="text-[var(--color-muted)]"> Read it on p{artefact.page}.</span>
          ) : null}
        </p>
      ))}

      {recovered.map((formula) => (
        <div key={formula.label} className="mb-3 last:mb-0">
          <p className="label mb-1.5">
            {formula.label} — recovered
            {formula.verifiedAt ? ', checked by a representative' : ', not yet checked'}
          </p>
          {/* The readable form. Monospaced because it is an expression. */}
          <p className="mb-1.5 font-[family-name:var(--font-mono)] text-[13px]">
            {formula.expression}
          </p>
          <dl className="mb-1.5 text-[11.5px] leading-snug text-[var(--color-muted)]">
            {Object.entries(formula.variables).map(([symbol, meaning]) => (
              <div key={symbol} className="flex gap-2">
                <dt className="w-4 shrink-0 font-[family-name:var(--font-mono)]">{symbol}</dt>
                <dd className="min-w-0">{meaning}</dd>
              </div>
            ))}
          </dl>
          {formula.recoveredFrom && (
            // Where it came from, so a reviewer can check the recovery rather
            // than trust it. This is the whole licence for showing a formula
            // the stored text does not contain.
            <p className="text-[11.5px] text-[var(--color-muted)]">
              Recovered from {formula.recoveredFrom}.
            </p>
          )}
        </div>
      ))}

      {unrecovered.map((formula) => (
        <div key={formula.label} className="mb-3 last:mb-0">
          <p className="label mb-1.5" style={{ color: 'var(--color-defect)' }}>
            {formula.label} — not recovered
          </p>
          {/*
           * Stated as a gap rather than filled with a plausible expression.
           * The variables are known and the expression is not, and a formula
           * that reads correctly but was reconstructed from the units would be
           * quoted at a table and checkable by nobody.
           */}
          <p className="text-[12.5px] leading-snug text-[var(--color-muted)]">{formula.note}</p>
        </div>
      ))}
    </section>
  );
}
